import { useContext } from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Login from './components/Login';
import MobileMyShifts from './components/MobileMyShifts';
import MobileAvailableShifts from './components/MobileAvailableShifts';
import Profile from './components/Profile';
import Messages from './components/Messages';
import ShiftAllocations from './components/ShiftAllocations';
import MobileAppShell from './components/MobileAppShell';
import AuthContext, { AuthContextProvider } from './contexts/AuthContext';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#f05a5a',
      light: '#de7f7f',
      dark: '#cf2e2e',
      contrastText: '#fff',
    },
    secondary: {
      main: '#3f51b5',
    },
    background: {
      default: '#060608',
      paper: 'rgba(255, 255, 255, 0.045)',
    },
    text: {
      primary: '#f5f1e6',
      secondary: '#c9c3b0',
    },
    divider: 'rgba(255, 255, 255, 0.09)',
    error: {
      main: '#f05a5a',
    },
    info: {
      main: '#3f51b5',
    },
  },
  typography: {
    fontFamily: "'Questrial', 'Roboto', Arial, sans-serif",
    fontSize: 16,
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: 'none',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: 'transparent',
          backgroundImage: 'none',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: '#1c1616',
          border: '1px solid rgba(240, 90, 90, 0.25)',
          color: '#f5f1e6',
          boxShadow: '0 16px 48px rgb(0 0 0 / 0.5)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(240, 90, 90, 0.12)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(240, 90, 90, 0.18)',
            '&:hover': {
              backgroundColor: 'rgba(240, 90, 90, 0.22)',
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgb(255 255 255 / 0.035)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&:hover fieldset': {
              borderColor: 'rgb(255 255 255 / 0.32)',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'rgb(240 90 90 / 0.55)',
            },
            // Override Chrome/Android autofill paint so floating labels stay readable
            '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active':
              {
                WebkitTextFillColor: '#f5f1e6',
                caretColor: '#f5f1e6',
                // inset box-shadow beats browser autofill background color
                WebkitBoxShadow: '0 0 0 100px #161412 inset',
                boxShadow: '0 0 0 100px #161412 inset',
                borderRadius: 'inherit',
                transition: 'background-color 99999s ease-in-out 0s',
              },
          },
          '& .MuiInputLabel-root': {
            color: '#8a8472',
          },
          '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            color: '#c9c3b0',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#c9c3b0',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          minHeight: 44,
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #de7f7f 0%, #f05a5a 45%, #cf2e2e 100%)',
          color: '#fff',
          boxShadow: '0 6px 18px rgba(240, 90, 90, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #de7f7f 0%, #f05a5a 45%, #cf2e2e 100%)',
            filter: 'brightness(1.05)',
            boxShadow: '0 6px 22px rgba(240, 90, 90, 0.4)',
          },
        },
        outlined: {
          borderColor: 'rgba(255, 255, 255, 0.09)',
          color: '#c9c3b0',
          '&:hover': {
            borderColor: 'rgba(240, 90, 90, 0.3)',
            backgroundColor: 'rgba(240, 90, 90, 0.06)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgb(63 81 181 / 0.2)',
          color: '#f5f1e6',
          border: '1px solid rgba(63, 81, 181, 0.3)',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardError: {
          backgroundColor: 'rgb(240 90 90 / 0.12)',
          color: '#f5f1e6',
          border: '1px solid rgba(240, 90, 90, 0.3)',
        },
      },
    },
  },
});

const PrivateRoute = ({ children, ...rest }) => {
  const { token } = useContext(AuthContext);

  return (
    <Route
      {...rest}
      render={({ location }) =>
        token ? (
          children
        ) : (
          <Redirect
            to={{
              pathname: '/login',
              state: { from: location },
            }}
          />
        )
      }
    />
  );
};

const AuthenticatedLayout = ({ children }) => <MobileAppShell>{children}</MobileAppShell>;

function AppContent() {
  const { token, isCheckingAuth } = useContext(AuthContext);

  if (isCheckingAuth) {
    return null;
  }

  return (
    <Router>
      <Switch>
        <Route path="/login">{token ? <Redirect to="/my-shifts" /> : <Login />}</Route>
        <Route path="/availabilities">
          <Redirect to="/my-shifts" />
        </Route>
        <Route path="/my-calendar">
          <Redirect to="/my-shifts" />
        </Route>
        <PrivateRoute path="/my-shifts">
          <AuthenticatedLayout>
            <MobileMyShifts />
          </AuthenticatedLayout>
        </PrivateRoute>
        <PrivateRoute path="/available-shifts">
          <AuthenticatedLayout>
            <MobileAvailableShifts />
          </AuthenticatedLayout>
        </PrivateRoute>
        <PrivateRoute path="/profile">
          <AuthenticatedLayout>
            <Profile />
          </AuthenticatedLayout>
        </PrivateRoute>
        <PrivateRoute path="/messages">
          <AuthenticatedLayout>
            <Messages />
          </AuthenticatedLayout>
        </PrivateRoute>
        <Route path="/notifications">
          <Redirect to="/messages" />
        </Route>
        <PrivateRoute path="/shift-allocations">
          <AuthenticatedLayout>
            <ShiftAllocations />
          </AuthenticatedLayout>
        </PrivateRoute>
        <Route exact path="/">
          <Redirect to={token ? '/my-shifts' : '/login'} />
        </Route>
      </Switch>
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthContextProvider>
        <AppContent />
      </AuthContextProvider>
    </ThemeProvider>
  );
}

export default App;
